import React, { useMemo } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { Scatter } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataset,
} from "chart.js";
import ChartDataLabels, { Context } from "chartjs-plugin-datalabels";
import { isEqual } from "lodash";
import { numericalString } from "../../../types";
import { useBaseTrackMap } from "../../Timing/BaseTrackMap";

ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
);

ChartJS.defaults.font.family = "JaguarExuberant, Helvetica, Arial, sans-serif";

function LiveAttackExpiry({ carColour }: { carColour: string }) {
  const driverGPS = useSelector((state: RootState) => state.driverGPS, isEqual);

  const attackModeStates = useSelector(
    (state: RootState) => state.attackMode.attackModeStates,
    isEqual,
  );

  const attackLapTime = useSelector(
    (state: RootState) =>
      state.raceData[carColour]?.raceConfig?.lapTimeAttack ?? null,
  );

  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
    isEqual,
  );

  const jagDrivers = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.jagDrivers,
    isEqual,
  );

  const refLapData = useSelector(
    (state: RootState) => state.imports.imports.strategy?.data.refLapData,
    isEqual,
  );

  const { containerRef, trackMap, rotatePoint, trackMapDatasets, bounds } =
    useBaseTrackMap({ showTimingLoops: true, showTurnNumbers: true });

  const expiryProjection = useMemo(() => {
    const refLapLat = refLapData?.[0];
    const refLapLon = refLapData?.[1];
    const refLapTime = refLapData?.[2];

    if (!refLapLat?.length || !refLapLon?.length || !refLapTime?.length) {
      return null;
    }

    const refLapTotalTime = refLapTime[refLapTime.length - 1];
    if (!refLapTotalTime) return null;

    const scaleFactor = attackLapTime ? attackLapTime / refLapTotalTime : 1;
    const lapDuration = refLapTotalTime * scaleFactor;

    const project = (importIndex: number, remainingSeconds: number) => {
      const currentTime = refLapTime[importIndex] * scaleFactor;
      let targetTime = (currentTime + remainingSeconds) % lapDuration;
      if (targetTime < 0) targetTime += lapDuration;

      let closestIndex = 0;
      let closestDiff = Infinity;
      for (let i = 0; i < refLapTime.length; i++) {
        const diff = Math.abs(refLapTime[i] * scaleFactor - targetTime);
        if (diff < closestDiff) {
          closestDiff = diff;
          closestIndex = i;
        }
      }

      return { lat: refLapLat[closestIndex], lon: refLapLon[closestIndex] };
    };

    return { project, lapDuration };
  }, [refLapData, attackLapTime]);

  const driverDatasets = useMemo(() => {
    if (!driverGPS || !attackModeStates || !expiryProjection) return [];

    const datasets: (ChartDataset<"scatter"> & {
      driverKey: string;
      labelBackgroundColor: string;
      driverFontColour: string;
      carTextColour: string;
      isJagCar: boolean;
      isDriver: boolean;
      isLapsLabel?: boolean;
    })[] = [];

    for (const [driverKey, attackModeState] of Object.entries(
      attackModeStates,
    )) {
      const lastActivation = attackModeState.attackActivations.at(-1);
      if (!lastActivation || lastActivation.endTime !== null) continue;

      const driverPosition = driverGPS[driverKey as numericalString];
      if (!driverPosition) {
        continue;
      }

      const allocatedDuration = attackModeState.attacksRemainingLive?.[0];
      if (allocatedDuration === undefined) continue;

      const elapsedSeconds = (Date.now() - lastActivation.startTime) / 1000;
      const remaining = allocatedDuration - elapsedSeconds;
      if (remaining <= 0) continue;

      const projected = expiryProjection.project(
        driverPosition.importIndex,
        remaining,
      );
      if (!projected) continue;

      // Whole laps left before the attack mode expires (integer division, not the wrap-around modulo above)
      const lapsRemaining = Math.floor(
        remaining / expiryProjection.lapDuration,
      );

      const info = driverInfo?.[driverKey];
      const driverName = info?.driverName || `Driver ${driverKey}`;
      const isJagCar = Object.values(jagDrivers || {}).includes(driverKey);
      const isHighlightedDriver =
        !!carColour && jagDrivers?.[carColour] === driverKey;
      const markerColor = info?.driverTeamColour || "#333333";
      const labelBackgroundColor = isJagCar
        ? info?.driverHighlightColourAlt || info?.driverTeamColour || "#333333"
        : "#ffffff";
      const driverFontColour = isJagCar
        ? info?.driverHighlightColourFontColourAlt ||
          info?.driverFontColour ||
          "#000000"
        : info?.driverTeamColour || "#333333";
      const carTextColour = info?.driverFontColour || "#ffffff";
      const label = `${driverName} (${Math.ceil(remaining)}s)`;

      datasets.push({
        label,
        data: [rotatePoint(projected.lon, projected.lat)],
        backgroundColor: markerColor,
        pointBorderColor: "#ffffff",
        pointRadius: isHighlightedDriver ? 6 : 5,
        pointBorderWidth: 1,
        driverKey,
        labelBackgroundColor,
        driverFontColour,
        carTextColour,
        isJagCar,
        isDriver: true,
      });

      // Invisible marker, used only to draw the laps-remaining count centered on the driver's marker
      datasets.push({
        label: `${lapsRemaining}`,
        data: [rotatePoint(projected.lon, projected.lat)],
        backgroundColor: "transparent",
        pointBorderColor: "transparent",
        pointRadius: 0,
        pointBorderWidth: 0,
        driverKey,
        labelBackgroundColor,
        driverFontColour,
        carTextColour,
        isJagCar,
        isDriver: true,
        isLapsLabel: true,
      });
    }

    return datasets.sort((a, b) => {
      return Number(b.isJagCar) - Number(a.isJagCar);
    });
  }, [
    driverGPS,
    attackModeStates,
    expiryProjection,
    driverInfo,
    jagDrivers,
    carColour,
    rotatePoint,
  ]);

  const chartData = useMemo(() => {
    return { datasets: [...driverDatasets, ...trackMapDatasets] };
  }, [trackMapDatasets, driverDatasets]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false as const,
      events: [],
      plugins: {
        datalabels: {
          display: (context: Context) => {
            // Only show labels for single point datasets
            return context.dataset.data.length === 1;
          },
          color: (context: Context) => {
            const dataset = context.dataset as {
              isDriver?: boolean;
              isLapsLabel?: boolean;
              driverFontColour?: string;
              carTextColour?: string;
            };
            if (!dataset.isDriver) return "#ffffff";
            if (dataset.isLapsLabel) return dataset.carTextColour || "#000000";
            return dataset.driverFontColour || "#000000";
          },
          backgroundColor: (context: Context) => {
            const dataset = context.dataset as {
              isDriver?: boolean;
              isLapsLabel?: boolean;
              labelBackgroundColor?: string;
            };
            if (!dataset.isDriver || dataset.isLapsLabel) return "transparent";
            return dataset.labelBackgroundColor || "#919191";
          },
          textStrokeColor: (context: Context) => {
            const dataset = context.dataset as { isDriver?: boolean };
            return dataset.isDriver ? "transparent" : "#2c2c2c";
          },
          textStrokeWidth: (context: Context) => {
            const dataset = context.dataset as { isDriver?: boolean };
            return dataset.isDriver ? 0 : 4;
          },
          borderRadius: 0,
          padding: (context: Context) => {
            const dataset = context.dataset as { isDriver?: boolean };
            return dataset.isDriver
              ? { top: 1, bottom: 1, left: 2, right: 2 }
              : { top: 0, bottom: 0, left: 0, right: 0 };
          },
          font: { size: 10, weight: "bold" as const },
          formatter: (_value: unknown, context: Context) => {
            return context.dataset.label || "";
          },
          anchor: "center" as const,
          align: (context: Context) => {
            const dataset = context.dataset as {
              isDriver?: boolean;
              isLapsLabel?: boolean;
            };
            if (!dataset.isDriver) return "center" as const;
            return dataset.isLapsLabel
              ? ("center" as const)
              : ("right" as const);
          },
          offset: (context: Context) => {
            const dataset = context.dataset as { isLapsLabel?: boolean };
            return dataset.isLapsLabel ? 0 : 8;
          },
        },
        legend: {
          display: false,
        },
        title: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      scales: {
        x: {
          type: "linear" as const,
          min: bounds.minLon,
          max: bounds.maxLon,
          title: { display: false },
          ticks: { display: false },
          grid: { display: false },
          border: { display: false },
        },
        y: {
          type: "linear" as const,
          min: bounds.minLat,
          max: bounds.maxLat,
          title: { display: false },
          ticks: { display: false },
          grid: { display: false },
          border: { display: false },
        },
      },
    }),
    [bounds],
  );

  const hasTrackData = trackMap?.trackEdges?.inner?.lat?.length > 0;

  return (
    <div className={styles.container} ref={containerRef}>
      {!hasTrackData && (
        <div className={styles.noDataOverlay}>Track map not configured</div>
      )}
      <Scatter data={chartData} options={chartOptions} />
    </div>
  );
}

export default LiveAttackExpiry;
